import * as AF from "@/components/form-items/AbstractForm";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SupportedLocale, supportedLocales } from "@/i18n";
import { Social, SocialType, socialTypes } from "@/lib/cms/cms-types";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { socialToIcon } from "../icons/socials/SocialIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import CmsFaviconUpload from "./CmsFaviconUpload";

export const GeneralSettingsForm = () => {
  const { t } = useTranslation();

  return (
    <div className="grid w-full grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-10">
      <AF.Col className="min-w-0">
        <div className="flex w-full flex-col gap-3">
          <p className="font-medium text-center text-gray-600">
            {t("cms.generalSettings.favicon.title")}
          </p>
          <div className="flex items-start gap-4">
            <CmsFaviconUpload />
            <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
              <p className="text-sm text-gray-500">
                {t("cms.generalSettings.favicon.help")}
              </p>
              <p className="text-sm text-slate-700">
                <span className="text-blue-500 underline">
                  {t("cms.generalSettings.favicon.clickCta")}
                </span>{" "}
                {t("cms.generalSettings.favicon.dragCta")}
              </p>
              <p className="text-xs text-slate-400">
                {t("cms.generalSettings.favicon.acceptedFormats")}
              </p>
            </div>
          </div>
        </div>
        <Separator />
        <AF.Switch
          name="headerAlwaysVisible"
          label={t("cms.generalSettings.headerAlwaysVisibleDesktop")}
        />
        <AF.Switch
          name="headerAlwaysVisibleMobile"
          label={t("cms.generalSettings.headerAlwaysVisibleMobile")}
        />
        <Separator />
        <VideoPlayerSettings />
        <Separator />
        <WebsiteLanguages />
      </AF.Col>

      <AF.Col className="min-w-0">
        <Socials />
      </AF.Col>
    </div>
  );
};

const VideoPlayerSettings = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold">
        {t("cms.generalSettings.player.title")}
      </p>
      <p className="text-xs text-slate-400">
        {t("cms.generalSettings.player.help")}
      </p>
      <AF.Switch
        name="playerControls"
        label={t("cms.generalSettings.player.controls")}
      />
      <AF.Switch
        name="playerAutoplay"
        label={t("cms.generalSettings.player.autoplay")}
      />
      <AF.Switch
        name="playerMuted"
        label={t("cms.generalSettings.player.muted")}
      />
      <AF.Switch
        name="playerLoop"
        label={t("cms.generalSettings.player.loop")}
      />
    </div>
  );
};

const WebsiteLanguages = () => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();

  const defaultWebsiteLanguage = useWatch({
    name: "defaultWebsiteLanguage",
  }) as SupportedLocale | undefined;
  const websiteLanguages = (useWatch({ name: "websiteLanguages" }) ??
    []) as SupportedLocale[];

  const handleDefaultChange = (lang: SupportedLocale) => {
    setValue("defaultWebsiteLanguage", lang, {
      shouldDirty: true,
      shouldValidate: true,
    });
    const current = getValues("websiteLanguages") as
      | SupportedLocale[]
      | undefined;
    if (!current?.includes(lang)) {
      setValue("websiteLanguages", [...(current ?? []), lang], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const toggleAdditionalLanguage = (
    lang: SupportedLocale,
    checked: boolean,
  ) => {
    if (lang === defaultWebsiteLanguage) return;

    const current = (getValues("websiteLanguages") ?? []) as SupportedLocale[];
    const withDefault = defaultWebsiteLanguage
      ? [...new Set([defaultWebsiteLanguage, ...current])]
      : current;

    if (checked) {
      setValue("websiteLanguages", [...new Set([...withDefault, lang])], {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const next = withDefault.filter((l) => l !== lang);
    setValue(
      "websiteLanguages",
      next.length > 0
        ? next
        : defaultWebsiteLanguage
          ? [defaultWebsiteLanguage]
          : next,
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <p className="font-medium text-center text-gray-600">
        {t("cms.generalSettings.websiteLanguages")}
      </p>
      <div className="flex flex-col gap-2">
        <Label className="text-sm text-gray-600">
          {t("cms.generalSettings.defaultWebsiteLanguage")}
        </Label>
        <MultilingualSelect
          value={defaultWebsiteLanguage ?? "fr"}
          onValueChange={(value) =>
            handleDefaultChange(value as SupportedLocale)
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-sm text-gray-600">
          {t("cms.generalSettings.additionalLanguages")}
        </Label>
        {supportedLocales.map((lang) => {
          const isDefault = lang === defaultWebsiteLanguage;
          const checked = isDefault || websiteLanguages.includes(lang);
          return (
            <div key={lang} className="flex items-center gap-2">
              <Checkbox
                id={`website-lang-${lang}`}
                checked={checked}
                disabled={isDefault}
                onCheckedChange={(value) =>
                  toggleAdditionalLanguage(lang, value === true)
                }
              />
              <Label
                htmlFor={`website-lang-${lang}`}
                className="text-sm font-normal cursor-pointer"
              >
                {t(`i18n.languages.${lang}.minified`)}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type SocialItemProps = {
  social: Social;
  index: number;
  handleRemoveSocial: (index: number) => void;
};

const SocialItem = ({ social, index, handleRemoveSocial }: SocialItemProps) => {
  const { t } = useTranslation();

  const SocialIcon = socialToIcon[social.socialType];

  return (
    <AF.Col
      className="rounded-xl border border-gray-200 bg-white p-3"
      key={social.socialType}
    >
      <AF.Row className="items-start justify-between gap-3">
        <SocialIcon
          className="h-9 w-9 shrink-0"
          color={social.colored ? undefined : "#9ca3af"}
        />
        <AF.Col className="min-w-0 flex-1 gap-2">
          <AF.Text
            name={`socials.${index}.url`}
            label={t(
              `cms.generalSettings.${social.socialType.toLowerCase()}`,
            )}
          />
          <AF.Switch
            className="w-fit"
            name={`socials.${index}.visible`}
            label={t("cms.generalSettings.visible")}
          />
          <AF.Switch
            className="w-fit"
            name={`socials.${index}.colored`}
            label={t("cms.generalSettings.colored")}
          />
        </AF.Col>
        <button
          type="button"
          className="shrink-0 cursor-pointer text-gray-400 hover:text-red-500"
          onClick={() => handleRemoveSocial(index)}
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </AF.Row>
    </AF.Col>
  );
};

const Socials = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { setValue } = useFormContext();
  const socials = useWatch({ name: "socials" });

  const handleAddSocial = (socialType: SocialType) => {
    const newSocial: Social = {
      socialType,
      url: "",
      visible: true,
      colored: true,
    };
    setValue("socials", [...socials, newSocial]);
    setOpen(false);
  };

  const handleRemoveSocial = (index: number) => {
    const currentSocials: Social[] = socials ?? [];
    const updated = currentSocials.filter((_, i) => i !== index);
    setValue("socials", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-center text-gray-600">
        {t("cms.generalSettings.socials")}
      </p>
      {socials?.map((social: Social, index: number) => (
        <SocialItem
          key={social.socialType}
          social={social}
          index={index}
          handleRemoveSocial={handleRemoveSocial}
        />
      ))}
      {socials && socials.length < socialTypes.length && (
        <AddSocialDialog
          open={open}
          setOpen={setOpen}
          handleAddSocial={handleAddSocial}
        />
      )}
    </div>
  );
};

type AddSocialDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleAddSocial: (socialType: SocialType) => void;
};

const AddSocialDialog = ({
  open,
  setOpen,
  handleAddSocial,
}: AddSocialDialogProps) => {
  const { t } = useTranslation();

  const socials = useWatch({ name: "socials" });

  const availableSocialTypes = socialTypes.filter(
    (socialType) =>
      !socials.some((social: Social) => social.socialType === socialType),
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="flex items-center cursor-pointer hover:underline text-brand-600">
          <Plus className="w-4 h-4" />
          <p className="text-sm">{t("cms.generalSettings.addSocial")}</p>
        </div>
      </DialogTrigger>
      <DialogContent needCloseButton={false} className="w-fit">
        <DialogHeader>
          <DialogTitle className="text-center">
            {t("cms.generalSettings.addSocial")}
          </DialogTitle>
        </DialogHeader>
        <div className="pt-4 flex flex-wrap gap-6 justify-center items-center">
          {availableSocialTypes.map((socialType) => {
            const SocialIcon = socialToIcon[socialType];
            return (
              <SocialIcon
                key={socialType}
                className="w-16 h-16 cursor-pointer hover:scale-110 transition-all duration-300"
                onClick={() => handleAddSocial(socialType)}
              />
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
